import Phaser from "phaser";
import { goTo } from "../ui/sceneTransition";
import { addQuitButton } from "../ui/menuUi";
import { GAME_HEIGHT, GAME_WIDTH } from "../config";
import { THEME, panel } from "../ui/theme";
import {
  signInWithEmail, signInWithOAuth, signOut,
  getSession, OAuthProvider,
} from "../backend/auth";
import {
  fetchMyDisplayName,
} from "../backend/profile";

const PROVIDERS: { id: OAuthProvider; label: string; bg: number }[] = [
  { id: "google",  label: "Continue with Google",  bg: 0x3a2a18 },
  { id: "discord", label: "Continue with Discord", bg: 0x2a2c5e },
];

/** Modal-style scene rendered on top of MenuScene. */
export class SignInScene extends Phaser.Scene {
  constructor() { super("SignInScene"); }

  create(): void {
    const dim = this.add.rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.65)
      .setOrigin(0).setInteractive();
    dim.on("pointerdown", () => this.close());

    const session = getSession();
    if (session) {
      this.drawAccountPanel();
      addQuitButton(this, () => this.close(), GAME_WIDTH / 2 + 204, GAME_HEIGHT / 2 - 100);
    } else {
      this.drawSignInPanel();
      addQuitButton(this, () => this.close(), GAME_WIDTH / 2 + 204, GAME_HEIGHT / 2 - 200);
    }

    const onEscape = () => this.close();
    this.input.keyboard?.on("keydown-ESC", onEscape);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.input.keyboard?.off("keydown-ESC", onEscape));
  }

  private close(): void {
    this.scene.stop();
    this.scene.resume("MenuScene");
    this.scene.get("MenuScene").scene.restart();
  }

  private drawSignInPanel(): void {
    const cx = GAME_WIDTH / 2, cy = GAME_HEIGHT / 2;
    const w = 480, h = 460;
    const px = cx - w / 2, py = cy - h / 2;
    const scroll = panel(this, px, py, w, h, "parchment");
    scroll.setInteractive();

    this.add.text(cx, py + 36, "SIGN IN", {
      fontFamily: THEME.fonts.display, fontSize: "30px", color: THEME.colors.iron
    }).setOrigin(0.5);

    PROVIDERS.forEach((p, i) => {
      const btnW = 360, btnH = 44;
      const bx = cx - btnW / 2, by = py + 90 + i * 56;
      const bg = panel(this, bx, by, btnW, btnH, "iron");
      bg.setTint(p.bg);
      bg.setInteractive({ useHandCursor: true });
      const lbl = this.add.text(cx, by + btnH / 2, p.label, {
        fontFamily: THEME.fonts.body, fontSize: "16px", color: THEME.colors.parchment
      }).setOrigin(0.5);
      bg.on("pointerover", () => bg.setAlpha(0.85));
      bg.on("pointerout",  () => bg.setAlpha(1));
      bg.on("pointerdown", async () => {
        const { error } = await signInWithOAuth(p.id);
        if (error) window.alert(`Sign-in failed: ${error}`);
      });
      void lbl;
    });

    this.add.text(cx, py + 290, "─── or ───", {
      fontFamily: THEME.fonts.body, fontSize: "15px", color: THEME.colors.lock
    }).setOrigin(0.5);

    const magicW = 360, magicH = 36;
    const mx = cx - magicW / 2, my = py + 320;
    const magicBg = panel(this, mx, my, magicW, magicH, "iron");
    magicBg.setInteractive({ useHandCursor: true });
    const magicLbl = this.add.text(cx, my + magicH / 2, "Email magic-link", {
      fontFamily: THEME.fonts.body, fontSize: "15px", color: THEME.colors.parchmentDim
    }).setOrigin(0.5);
    magicBg.on("pointerover", () => { magicBg.setTint(0xffeec0); magicLbl.setColor(THEME.colors.parchment); });
    magicBg.on("pointerout",  () => { magicBg.clearTint(); magicLbl.setColor(THEME.colors.parchmentDim); });
    magicBg.on("pointerdown", async () => {
      const email = window.prompt("Email for magic-link sign-in:");
      if (!email) return;
      const { error } = await signInWithEmail(email.trim());
      window.alert(error ? `Failed: ${error}` : "Magic link sent. Check your email.");
    });

    const close = this.add.text(cx, py + h - 30, "cancel", {
      fontFamily: THEME.fonts.body, fontSize: "15px", color: THEME.colors.lock
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    close.on("pointerover", () => close.setColor(THEME.colors.blood));
    close.on("pointerout",  () => close.setColor(THEME.colors.lock));
    close.on("pointerdown", () => this.close());
  }

  private async drawAccountPanel(): Promise<void> {
    const cx = GAME_WIDTH / 2, cy = GAME_HEIGHT / 2;
    const w = 480, h = 260;
    const px = cx - w / 2, py = cy - h / 2;
    const scroll = panel(this, px, py, w, h, "parchment");
    scroll.setInteractive();

    this.add.text(cx, py + 30, "SIGNED IN", {
      fontFamily: THEME.fonts.display, fontSize: "28px", color: THEME.colors.iron
    }).setOrigin(0.5);

    const session = getSession()!;
    this.add.text(cx, py + 70, session.user.email ?? "(no email)", {
      fontFamily: THEME.fonts.body, fontSize: "15px", color: THEME.colors.lock
    }).setOrigin(0.5);

    const nameText = this.add.text(cx, py + 105, "Loading display name…", {
      fontFamily: THEME.fonts.display, fontSize: "18px", color: THEME.colors.iron
    }).setOrigin(0.5);
    const currentName = await fetchMyDisplayName();
    if (!this.scene.isActive() || !nameText.active) return;
    nameText.setText(`Welcome, ${currentName ?? "Player"}`);

    const profW = 240, profH = 36;
    const prfx = cx - profW / 2, prfy = py + 140;
    const profBg = panel(this, prfx, prfy, profW, profH, "iron");
    profBg.setInteractive({ useHandCursor: true });
    const profLbl = this.add.text(cx, prfy + profH / 2, "view profile", {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.gold
    }).setOrigin(0.5);
    profBg.on("pointerover", () => { profBg.setTint(0xffeec0); profLbl.setColor(THEME.colors.parchment); });
    profBg.on("pointerout",  () => { profBg.clearTint(); profLbl.setColor(THEME.colors.gold); });
    profBg.on("pointerdown", () => goTo(this, "ProfileScene", { stop: ["MenuScene"] }));

    const soW = 240, soH = 36;
    const sx = cx - soW / 2, sy = py + 190;
    const soBg = panel(this, sx, sy, soW, soH, "iron");
    soBg.setInteractive({ useHandCursor: true });
    const soLbl = this.add.text(cx, sy + soH / 2, "sign out", {
      fontFamily: THEME.fonts.display, fontSize: "16px", color: THEME.colors.gold
    }).setOrigin(0.5);
    soBg.on("pointerover", () => { soBg.setTint(0xffeec0); soLbl.setColor(THEME.colors.parchment); });
    soBg.on("pointerout",  () => { soBg.clearTint(); soLbl.setColor(THEME.colors.gold); });
    soBg.on("pointerdown", async () => { await signOut(); this.close(); });

    const close = this.add.text(cx, py + h - 30, "close", {
      fontFamily: THEME.fonts.body, fontSize: "14px", color: THEME.colors.lock
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    close.on("pointerover", () => close.setColor(THEME.colors.blood));
    close.on("pointerout",  () => close.setColor(THEME.colors.lock));
    close.on("pointerdown", () => this.close());
  }
}
